import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SBINModal from "./SBINModal";
import {
  streamAiChat,
  sendAiChatWithAttachments,
  getAiCapabilities,
  getAiHealth,
  exportAiTable,
} from "../../api/aiAssistant";
import { useAuth } from "../../context/AuthContext";
import { getEllyannaAccessGate } from "../../utils/ellyannaAccessGate";

const AI_TEMPORARILY_UNAVAILABLE_MESSAGE =
  "The Ellyanna AI assistant is temporarily unavailable. Please try again in a moment.";
const ACTIVE_SCHEDULE_APPOINTMENT_STORAGE_KEY = "ellyanna:scheduleActiveAppointment";

const buildGreeting = () => {
  return {
    role: "assistant",
    content:
      "Hi, I can help with appointments, clients, reports, and setup. I will only use features you have access to.",
  };
};

const buildPlaceholder = () => {
  return "Ask Ellyanna...";
};

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const parseIsoDate = (value) => {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  return { year, month, day };
};

const addDaysIso = (isoDate, days) => {
  const parts = parseIsoDate(isoDate);
  if (!parts) return "";
  const base = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
};

const monthStartIso = (isoDate) => {
  const parts = parseIsoDate(isoDate);
  if (!parts) return "";
  const month = String(parts.month).padStart(2, "0");
  return `${parts.year}-${month}-01`;
};

const MAX_CSV_ROWS = 10000;
const MAX_PDF_ROWS = 2000;
const DEFAULT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const DEFAULT_FILE_TYPES = ["application/pdf", "text/plain", "text/csv"];
const ATTACHMENT_DROP_HINT = "Drop to attach (PNG/JPG/WEBP/PDF/TXT/CSV)";
const STREAM_STATUS_LABELS = {
  routing: "Routing...",
  fetching_data: "Fetching report...",
  writing: "Writing...",
};
const FALLBACK_AI_ERROR_MESSAGE =
  "Sorry, I couldn't process that request. Please try again.";
const SUPPORT_ACTION_ALLOWLIST = new Set([
  "navigate",
  "open_modal",
  "highlight",
  "prefill",
]);
const SUPPORT_ROUTE_ALLOWLIST = ["/manager/schedule", "/schedule"];
const SUPPORT_SELECTOR_ALLOWLIST = [
  "[data-testid='new-appointment-btn']",
  "[data-testid='reschedule-btn']",
];
const SUPPORT_PREFILL_ALLOWLIST = new Set(["clientSearch", "date", "time"]);

const AIAssistantHeaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5 shrink-0"
    aria-hidden="true"
  >
    <path d="M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
    <path d="M14 9l.8 1.6 1.7.4-1.7.6L14 13l-.8-1.4-1.7-.6 1.7-.4L14 9z" />
  </svg>
);

const formatAiError = (detail) => {
  if (!detail || typeof detail !== "string") {
    return FALLBACK_AI_ERROR_MESSAGE;
  }
  const lowered = detail.toLowerCase();
  if (lowered.includes("openai")) {
    return FALLBACK_AI_ERROR_MESSAGE;
  }
  if (lowered.includes("ai_assistant_unavailable")) {
    return "AI Assistant is unavailable. Please try again later.";
  }
  if (lowered.includes("ai_assistant_failed")) {
    return FALLBACK_AI_ERROR_MESSAGE;
  }
  if (lowered.includes("ai provider")) {
    return FALLBACK_AI_ERROR_MESSAGE;
  }
  if (lowered.includes("ai authentication")) {
    return "AI authentication error. Please contact support.";
  }
  return FALLBACK_AI_ERROR_MESSAGE;
};

const parseSupportPayload = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && parsed.mode === "support") {
      return parsed;
    }
  } catch (err) {
    return null;
  }
  return null;
};

const labelForSupportAction = (action) => {
  const type = String(action?.type || "");
  if (type === "navigate") return "Schedule";
  if (type === "open_modal") {
    if (action?.id === "newAppointment") return "New Appointment";
    return "Open";
  }
  if (type === "highlight") {
    if (action?.selector === "[data-testid='new-appointment-btn']") {
      return "New Appointment";
    }
    if (action?.selector === "[data-testid='reschedule-btn']") {
      return "Reschedule";
    }
    return "Highlight";
  }
  if (type === "prefill") {
    if (action?.field === "clientSearch") return "Client search";
    if (action?.field === "date") return "Date";
    if (action?.field === "time") return "Time";
  }
  return "Try this now";
};

const NAV_PAGE_POLICIES = {
  CLIENTS_LIST: {
    path: "/clients",
    requiredAnyPermissions: ["clients.view_contact", "clients.manage"],
    allowedRoles: ["owner", "manager", "receptionist", "technician", "apprentice"],
  },
  CLIENT_EDIT: {
    path: "/clients",
    requiredAnyPermissions: ["clients.manage"],
    allowedRoles: ["owner", "manager", "receptionist", "technician", "apprentice"],
  },
  MANAGER_SCHEDULE: {
    path: "/manager/schedule",
    requiredAllPermissions: ["schedule.view_all", "schedule.edit"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  TICKETS_LIST: {
    path: "/tickets",
    requiredAnyPermissions: ["tickets.manage"],
    allowedRoles: ["owner", "manager", "receptionist", "technician", "apprentice"],
  },
  MANAGER_REPORTS_HOME: {
    path: "/manager/reports",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_TOTAL_SHEET: {
    path: "/manager/reports/total-sheet",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_TAX_LIABILITY: {
    path: "/manager/reports/tax-liability",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_DAILY_CLOSING: {
    path: "/manager/reports/daily-closing",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_CASH_BALANCE: {
    path: "/manager/reports/cash-balance",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_SALES_BY_EMPLOYEE: {
    path: "/manager/reports/sales-by-employee",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  REPORT_REWARDS_ANALYTICS: {
    path: "/manager/reports/rewards",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist"],
  },
  REPORT_TRAINING_PROGRESS: {
    path: "/manager/reports/training-progress",
    requiredAnyPermissions: ["reports.view"],
    allowedRoles: ["owner", "manager", "receptionist"],
  },
  HR_TIME_CLOCK: {
    path: "/manager/time-clock",
    requiredAnyPermissions: ["timeclock.view"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  HR_PAYROLL: {
    path: "/manager/payroll",
    requiredAnyPermissions: ["payroll.view", "payroll.manage"],
    allowedRoles: ["owner", "manager"],
  },
  HR_PAY_STUBS: {
    path: "/manager/hr/pay-stubs",
    requiredAnyPermissions: ["payroll.manage"],
    allowedRoles: ["owner", "manager"],
  },
  MANAGE_STAFF: {
    path: "/manager/manage/staff",
    requiredAnyPermissions: ["staff.manage"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
  MANAGE_SERVICES: {
    path: "/manager/manage/services",
    requiredAnyPermissions: ["services.manage"],
    allowedRoles: ["owner", "manager", "receptionist", "technician"],
  },
};

const normalizeRole = (role) => {
  const raw = (role || "").toLowerCase().trim();
  if (!raw) return "technician";
  if (raw === "reception") return "receptionist";
  if (raw === "solo_technician") return "technician";
  if (raw === "admin" || raw === "host") return "owner";
  if (["owner", "manager", "receptionist", "technician", "apprentice"].includes(raw)) {
    return raw;
  }
  return "technician";
};

const hasAnyPermission = (perms, keys) =>
  Array.isArray(keys) && keys.some((key) => Boolean(perms?.[key]));

const hasAllPermissions = (perms, keys) =>
  Array.isArray(keys) && keys.every((key) => Boolean(perms?.[key]));

const isPolicyAllowedByPermissions = (perms, policy) => {
  if (!policy) return false;
  const requiredAll = policy.requiredAllPermissions || [];
  const requiredAny = policy.requiredAnyPermissions || [];
  if (requiredAll.length && !hasAllPermissions(perms, requiredAll)) return false;
  if (requiredAny.length && !hasAnyPermission(perms, requiredAny)) return false;
  return Boolean(requiredAll.length || requiredAny.length);
};

const parseNavPath = (path) => {
  if (typeof path !== "string" || !path) return null;
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const url = new URL(path, base);
    return { pathname: url.pathname, searchParams: url.searchParams };
  } catch (err) {
    const [pathname, query = ""] = path.split("?");
    return { pathname, searchParams: new URLSearchParams(query) };
  }
};

const normalizeLegacySchedulePath = (path) => {
  const parsed = parseNavPath(path);
  if (!parsed) return path;
  const { pathname, searchParams } = parsed;
  if (!pathname.startsWith("/manager-schedule")) {
    return path;
  }
  const query = searchParams.toString();
  return query ? `/manager/schedule?${query}` : "/manager/schedule";
};

const parseFilename = (headerValue) => {
  if (!headerValue) return "export";
  const match = /filename=([^;]+)/i.exec(headerValue);
  if (!match) return "export";
  return match[1].replace(/"/g, "").trim() || "export";
};

const last4 = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.slice(-4);
};

const buildCandidateLabel = (candidate, index) => {
  const name = candidate?.name || "Client";
  const phoneEnding = last4(candidate?.phone);
  const email = candidate?.email || "";
  let detail = "";
  if (phoneEnding) {
    detail = `phone ending ${phoneEnding}`;
  } else if (email) {
    detail = email;
  }
  const option = String.fromCharCode(65 + index);
  return detail ? `Option ${option}: ${name} (${detail})` : `Option ${option}: ${name}`;
};

const buildCandidateMessage = (candidate) => {
  const name = candidate?.name || "the client";
  const phone = candidate?.phone || "";
  const email = candidate?.email || "";
  const clientId = candidate?.client_id;
  if (phone) {
    return `It's ${name} with phone ${phone}.`;
  }
  if (email) {
    return `It's ${name} with email ${email}.`;
  }
  if (clientId) {
    return `Use client id ${clientId}.`;
  }
  return `It's ${name}.`;
};

const formatPrice = (value) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return null;
  return `$${numberValue.toFixed(2)}`;
};

const formatFileSize = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("FILE_READ_FAILED"));
    reader.readAsDataURL(file);
  });

const buildServiceLine = (service, fieldsUsed = []) => {
  const name = service?.name || "Service";
  const details = [];
  if (fieldsUsed.includes("id") && service?.id) {
    details.push(`ID ${service.id}`);
  }
  const price = formatPrice(service?.price);
  if (price) {
    details.push(price);
  }
  if (service?.duration_minutes) {
    details.push(`${service.duration_minutes} min`);
  }
  if (service?.category) {
    details.push(service.category);
  }
  return details.length ? `${name} - ${details.join(" | ")}` : name;
};

const blobToJson = async (blob) => {
  if (!blob) return null;
  try {
    const text = await blob.text();
    return JSON.parse(text);
  } catch (err) {
    return null;
  }
};

const inferPageFromPath = (path) => {
  const parsed = parseNavPath(path);
  if (!parsed) return null;
  const { pathname, searchParams } = parsed;
  if (pathname === "/manager/reports/total-sheet") return "REPORT_TOTAL_SHEET";
  if (pathname === "/manager/reports/tax-liability") return "REPORT_TAX_LIABILITY";
  if (pathname === "/manager/reports/daily-closing") return "REPORT_DAILY_CLOSING";
  if (pathname === "/manager/reports/cash-balance") return "REPORT_CASH_BALANCE";
  if (pathname === "/manager/reports/sales-by-employee") return "REPORT_SALES_BY_EMPLOYEE";
  if (pathname === "/manager/reports/rewards") return "REPORT_REWARDS_ANALYTICS";
  if (pathname === "/manager/reports/training-progress") return "REPORT_TRAINING_PROGRESS";
  if (pathname === "/manager/reports") return "MANAGER_REPORTS_HOME";
  if (pathname === "/manager-schedule") return "MANAGER_SCHEDULE";
  if (pathname === "/manager/schedule") return "MANAGER_SCHEDULE";
  if (pathname === "/technician/schedule") return "TECHNICIAN_SCHEDULE";
  if (pathname === "/manager/time-clock") return "HR_TIME_CLOCK";
  if (pathname === "/manager/payroll") return "HR_PAYROLL";
  if (pathname === "/manager/hr/pay-stubs") return "HR_PAY_STUBS";
  if (pathname === "/manager/manage/staff") return "MANAGE_STAFF";
  if (pathname === "/manager/manage/services") return "MANAGE_SERVICES";
  if (pathname === "/tickets") return "TICKETS_LIST";
  if (pathname === "/clients") {
    return searchParams.has("edit_client") ? "CLIENT_EDIT" : "CLIENTS_LIST";
  }
  return null;
};

const isPathAllowedForPage = (page, path) => {
  const policy = NAV_PAGE_POLICIES[page];
  if (!policy) return false;
  const parsed = parseNavPath(path);
  if (!parsed) return false;
  const { pathname, searchParams } = parsed;
  if (pathname !== policy.path) return false;
  if (page === "CLIENT_EDIT") {
    const idValue = searchParams.get("edit_client");
    const id = Number(idValue);
    return Number.isFinite(id) && id > 0;
  }
  if (page === "CLIENTS_LIST") {
    return !searchParams.has("edit_client");
  }
  return true;
};

const setInputValue = (input, nextValue) => {
  if (!input) return false;
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  if (!setter) return false;
  setter.call(input, nextValue);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
};

export default function AIAssistantModal({
  isOpen,
  onClose,
  initialPrefillMessage,
  initialContext,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentSalon, salonTimezone } = useAuth();
  const salonId =
    user?.salon_id ??
    currentSalon?.id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id"))
      : null);
  const accessGate = useMemo(
    () => getEllyannaAccessGate(user?.role, user),
    [user]
  );
  const storageKey = useMemo(
    () => (salonId ? `ai_assistant_history_${salonId}` : null),
    [salonId]
  );
  const [capabilities, setCapabilities] = useState(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [messages, setMessages] = useState([buildGreeting([])]);
  const [input, setInput] = useState("");
  const [nextContextOverride, setNextContextOverride] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportState, setExportState] = useState({});
  const [pendingClientPick, setPendingClientPick] = useState(null);
  const [pendingAppointmentPick, setPendingAppointmentPick] = useState(null);
  const [pendingCancelAction, setPendingCancelAction] = useState(null);
  const [pendingRescheduleAction, setPendingRescheduleAction] = useState(null);
  const [supportNotice, setSupportNotice] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreviewUrl, setAttachedPreviewUrl] = useState(null);
  const [attachError, setAttachError] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageName, setPreviewImageName] = useState("");
  const [activeShortcutCategory, setActiveShortcutCategory] = useState("appointments");
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachRequestId = useRef(0);
  const dragCounterRef = useRef(0);
  const streamAbortRef = useRef(null);
  const noClientEditPermissionMessage =
    "You don't have permission to edit client records.";

  const allowedTools = useMemo(
    () => (Array.isArray(capabilities?.allowed_tools) ? capabilities.allowed_tools : []),
    [capabilities]
  );
  const permissions = useMemo(
    () => (capabilities?.permissions && typeof capabilities.permissions === "object"
      ? capabilities.permissions
      : {}),
    [capabilities]
  );
  const canViewReports = useMemo(
    () =>
      allowedTools.includes("get_sales_summary") ||
      allowedTools.includes("get_refunds_summary") ||
      allowedTools.includes("get_training_progress_summary") ||
      allowedTools.includes("get_training_progress_report") ||
      allowedTools.includes("get_rewards_analytics_summary") ||
      allowedTools.includes("get_rewards_analytics_report") ||
      allowedTools.includes("run_report") ||
      allowedTools.includes("list_reports"),
    [allowedTools]
  );
  const canAccessClients = useMemo(
    () => Boolean(permissions?.["clients.manage"] || permissions?.["clients.view_contact"]),
    [permissions]
  );
  const canManageClients = useMemo(
    () => Boolean(permissions?.["clients.manage"]),
    [permissions]
  );
  const canUseFullSchedule = useMemo(
    () => Boolean(permissions?.["schedule.view_all"] && permissions?.["schedule.edit"]),
    [permissions]
  );
  const canViewSchedule = useMemo(
    () => Boolean(permissions?.["schedule.view_all"] || permissions?.["schedule.view_own"]),
    [permissions]
  );
  const canManageServices = useMemo(
    () => Boolean(permissions?.["services.manage"]),
    [permissions]
  );
  const canManageTickets = useMemo(
    () => Boolean(permissions?.["tickets.manage"]),
    [permissions]
  );
  const canManageStaff = useMemo(
    () => Boolean(permissions?.["staff.manage"]),
    [permissions]
  );
  const canManageSettings = useMemo(
    () => Boolean(permissions?.["settings.manage"]),
    [permissions]
  );
  const canManageUserRoles = useMemo(
    () => Boolean(permissions?.["users.manage_roles"]),
    [permissions]
  );
  const canAccessPayroll = useMemo(
    () => Boolean(permissions?.["payroll.view"] || permissions?.["payroll.manage"]),
    [permissions]
  );
  const canAccessTraining = useMemo(
    () => Boolean(permissions?.["training.view"] || permissions?.["training.manage"]),
    [permissions]
  );
  const canViewRewards = useMemo(
    () => Boolean(permissions?.["rewards.view"]),
    [permissions]
  );
  const effectiveTimezone = capabilities?.timezone || salonTimezone || undefined;
  const currentRoute =
    (location
      ? `${location.pathname || ""}${location.search || ""}`
      : "") ||
    (typeof window !== "undefined"
      ? `${window.location?.pathname || ""}${window.location?.search || ""}`
      : "") ||
    "";
  const currentPage = useMemo(
    () => inferPageFromPath(currentRoute),
    [currentRoute]
  );
  const currentPageKey = currentPage ? currentPage.toLowerCase() : null;
  const defaultShortcutCategory = useMemo(() => {
    if (currentPage === "MANAGER_SCHEDULE" || currentPage === "TECHNICIAN_SCHEDULE") {
      return "appointments";
    }
    if (currentPage === "CLIENTS_LIST" || currentPage === "CLIENT_EDIT") {
      return "clients";
    }
    if (currentPage && (currentPage.startsWith("REPORT_") || currentPage === "MANAGER_REPORTS_HOME")) {
      return "reports";
    }
    return "setup";
  }, [currentPage]);
  const routeAppointmentContext = useMemo(() => {
    try {
      const query = currentRoute.includes("?")
        ? currentRoute.slice(currentRoute.indexOf("?"))
        : "";
      const params = new URLSearchParams(query);
      const appointmentId = Number(
        params.get("appointmentId") || params.get("highlightAppointmentId")
      );
      const dateValue = params.get("date") || "";
      if (
        (!Number.isFinite(appointmentId) || appointmentId <= 0) &&
        currentRoute.startsWith("/manager/schedule") &&
        typeof window !== "undefined"
      ) {
        try {
          const stored = JSON.parse(
            window.sessionStorage.getItem(ACTIVE_SCHEDULE_APPOINTMENT_STORAGE_KEY) ||
              "{}"
          );
          const storedAppointmentId = Number(stored.current_appointment_id);
          const storedDate = String(stored.current_appointment_date || "");
          return {
            current_appointment_id:
              Number.isFinite(storedAppointmentId) && storedAppointmentId > 0
                ? storedAppointmentId
                : undefined,
            current_appointment_date: /^\d{4}-\d{2}-\d{2}$/.test(storedDate)
              ? storedDate
              : undefined,
          };
        } catch (err) {
          return {};
        }
      }
      return {
        current_appointment_id:
          Number.isFinite(appointmentId) && appointmentId > 0
            ? appointmentId
            : undefined,
        current_appointment_date: /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
          ? dateValue
          : undefined,
      };
    } catch (err) {
      return {};
    }
  }, [currentRoute]);
  const supportUiCapabilities = useMemo(
    () => ({
      can_navigate: canViewSchedule,
      can_open_modal: currentPage === "MANAGER_SCHEDULE" && canUseFullSchedule,
      can_highlight: currentPage === "MANAGER_SCHEDULE" && canUseFullSchedule,
      can_prefill: currentPage === "MANAGER_SCHEDULE" && canUseFullSchedule,
    }),
    [canUseFullSchedule, canViewSchedule, currentPage]
  );
  const supportContext = useMemo(
    () => ({
      current_route: currentRoute || undefined,
      current_page_key: currentPageKey || undefined,
      current_appointment_id: routeAppointmentContext.current_appointment_id,
      current_appointment_date: routeAppointmentContext.current_appointment_date,
      ui_capabilities: supportUiCapabilities,
    }),
    [currentRoute, currentPageKey, routeAppointmentContext, supportUiCapabilities]
  );
  useEffect(() => {
    if (!isOpen) return;
    if (typeof initialPrefillMessage === "string" && initialPrefillMessage.trim()) {
      setInput(initialPrefillMessage);
    }
    if (initialContext && typeof initialContext === "object") {
      setNextContextOverride(initialContext);
    }
  }, [isOpen, initialPrefillMessage, initialContext]);
  useEffect(() => {
    if (!isOpen) {
      setNextContextOverride(null);
    }
  }, [isOpen]);
  const defaultGreeting = useMemo(() => buildGreeting(), []);
  const inputPlaceholder = useMemo(() => buildPlaceholder(), []);
  const uploadsEnabled = Boolean(
    capabilities?.uploads_enabled ?? capabilities?.uploadsEnabled
  );
  const imageInputsEnabled = Boolean(
    capabilities?.image_inputs_enabled ?? capabilities?.imageInputsEnabled
  );
  const fileInputsEnabled = Boolean(
    capabilities?.file_inputs_enabled ?? capabilities?.fileInputsEnabled
  );
  const uploadLimits =
    capabilities?.upload_limits ?? capabilities?.uploadLimits ?? {};
  const maxUploadMb = Number(uploadLimits?.max_mb || 0);
  const maxUploadBytes = maxUploadMb > 0 ? maxUploadMb * 1024 * 1024 : 0;
  const maxTextChars = Number(uploadLimits?.max_text_chars || 0);
  const canAttachImage = uploadsEnabled && imageInputsEnabled;
  const canAttachFile = uploadsEnabled && fileInputsEnabled;
  const attachmentsAvailable =
    uploadsEnabled && (imageInputsEnabled || fileInputsEnabled);
  const attachmentControlsDisabled =
    isLoading || capabilitiesLoading || healthLoading || !aiConfigured;
  const allowedImageTypes = useMemo(() => {
    const types = capabilities?.allowed_upload_mime_types?.images;
    return Array.isArray(types) ? types : DEFAULT_IMAGE_TYPES;
  }, [capabilities]);
  const allowedFileTypes = useMemo(() => {
    const types = capabilities?.allowed_upload_mime_types?.files;
    return Array.isArray(types) ? types : DEFAULT_FILE_TYPES;
  }, [capabilities]);
  const textFileTypes = useMemo(() => ["text/plain", "text/csv"], []);
  const allowedAttachmentTypes = useMemo(() => {
    const types = [];
    if (canAttachImage) {
      types.push(...allowedImageTypes);
    }
    if (canAttachFile) {
      types.push(...allowedFileTypes);
    }
    return types;
  }, [allowedImageTypes, allowedFileTypes, canAttachImage, canAttachFile]);

  useEffect(() => {
    if (!storageKey) return;
    const stored = window.localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      } catch (err) {
        // ignore parse errors
      }
    }
    setMessages([buildGreeting([])]);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    const sanitized = messages.slice(-50).map((msg) => {
      if (!msg?.attachment) return msg;
      const { attachment, ...rest } = msg;
      return rest;
    });
    window.localStorage.setItem(storageKey, JSON.stringify(sanitized));
  }, [messages, storageKey]);

  useEffect(() => {
    if (!capabilities) return;
    setMessages((prev) => {
      const hasUserMessage = prev.some((msg) => msg.role === "user");
      if (hasUserMessage) {
        return prev;
      }
      if (
        prev.length === 1 &&
        prev[0]?.role === "assistant" &&
        prev[0]?.content !== defaultGreeting.content
      ) {
        return [defaultGreeting];
      }
      return prev;
    });
  }, [capabilities, defaultGreeting]);

  useEffect(() => {
    if (!isOpen) return;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading, isOpen]);

  useEffect(() => {
    if (isOpen) return;
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (attachedPreviewUrl) {
        URL.revokeObjectURL(attachedPreviewUrl);
      }
    };
  }, [attachedPreviewUrl]);

  const resetAttachmentInputs = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [fileInputRef]);

  const clearAttachments = () => {
    attachRequestId.current += 1;
    if (attachedPreviewUrl) {
      URL.revokeObjectURL(attachedPreviewUrl);
    }
    setAttachedImage(null);
    setAttachedFile(null);
    setAttachedPreviewUrl(null);
    setAttachError("");
    resetAttachmentInputs();
  };

  const attachFile = useCallback(
    async (file) => {
      if (!file) return;
      const requestId = ++attachRequestId.current;
      const applyIfCurrent = (fn) => {
        if (requestId !== attachRequestId.current) return;
        fn();
      };
      const mimeType = (file.type || "").toLowerCase();
      if (allowedImageTypes.includes(mimeType)) {
        if (!canAttachImage) {
          applyIfCurrent(() =>
            setAttachError("Image uploads are not enabled on this server.")
          );
          resetAttachmentInputs();
          return;
        }
        if (maxUploadBytes && file.size > maxUploadBytes) {
          applyIfCurrent(() =>
            setAttachError(`Image is too large. Max size is ${maxUploadMb} MB.`)
          );
          resetAttachmentInputs();
          return;
        }
        if (attachedPreviewUrl) {
          URL.revokeObjectURL(attachedPreviewUrl);
        }
        applyIfCurrent(() => {
          setAttachError("");
          setAttachedFile(null);
          setAttachedImage(file);
          setAttachedPreviewUrl(URL.createObjectURL(file));
        });
        return;
      }

      if (allowedFileTypes.includes(mimeType)) {
        if (!canAttachFile) {
          applyIfCurrent(() =>
            setAttachError("File uploads are not enabled on this server.")
          );
          resetAttachmentInputs();
          return;
        }
        if (maxUploadBytes && file.size > maxUploadBytes) {
          applyIfCurrent(() =>
            setAttachError(`File is too large. Max size is ${maxUploadMb} MB.`)
          );
          resetAttachmentInputs();
          return;
        }
        if (maxTextChars && textFileTypes.includes(mimeType)) {
          try {
            const text = await file.text();
            if (text.length > maxTextChars) {
              applyIfCurrent(() =>
                setAttachError(
                  `Text file is too long. Max ${maxTextChars.toLocaleString()} characters.`
                )
              );
              resetAttachmentInputs();
              return;
            }
          } catch (err) {
            applyIfCurrent(() =>
              setAttachError("Unable to read that text file. Try again.")
            );
            resetAttachmentInputs();
            return;
          }
        }
        if (attachedPreviewUrl) {
          URL.revokeObjectURL(attachedPreviewUrl);
        }
        applyIfCurrent(() => {
          setAttachError("");
          setAttachedImage(null);
          setAttachedPreviewUrl(null);
          setAttachedFile(file);
        });
        return;
      }

      applyIfCurrent(() =>
        setAttachError(
          "Unsupported file type. Use PDF, TXT, CSV, PNG, JPG, or WEBP."
        )
      );
      resetAttachmentInputs();
    },
    [
      allowedFileTypes,
      allowedImageTypes,
      attachedPreviewUrl,
      canAttachFile,
      canAttachImage,
      maxTextChars,
      maxUploadBytes,
      maxUploadMb,
      resetAttachmentInputs,
      textFileTypes,
    ]
  );

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    attachFile(file);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    if (!attachmentsAvailable) return;
    if (attachmentControlsDisabled) return;
    const types = Array.from(event.dataTransfer?.types || []);
    if (types.length > 0 && !types.includes("Files")) return;
    dragCounterRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (!attachmentsAvailable) return;
    if (attachmentControlsDisabled) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragActive(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!attachmentsAvailable) return;
    if (attachmentControlsDisabled) return;
    const types = Array.from(event.dataTransfer?.types || []);
    if (types.length > 0 && !types.includes("Files")) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    if (attachmentControlsDisabled) {
      setAttachError("Finish the current message before attaching a file.");
      return;
    }
    if (!attachmentsAvailable) {
      setAttachError("Attachments are not enabled on this server.");
      return;
    }
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;
    const supported = files.find((item) =>
      allowedAttachmentTypes.includes((item?.type || "").toLowerCase())
    );
    if (!supported) {
      setAttachError(
        "Unsupported file type. Use PDF, TXT, CSV, PNG, JPG, or WEBP."
      );
      return;
    }
    attachFile(supported);
  };

  const handlePaste = useCallback(
    (event) => {
      if (!event?.clipboardData?.items || event.defaultPrevented) return;
      const items = Array.from(event.clipboardData.items || []);
      const fileItem = items.find((item) => item.kind === "file");
      if (!fileItem) return;
      const file = fileItem.getAsFile();
      if (!file) return;
      if (attachmentControlsDisabled) {
        setAttachError("Finish the current message before attaching a file.");
        return;
      }
      if (!attachmentsAvailable) {
        setAttachError("Attachments are not enabled on this server.");
        return;
      }
      const mimeType = (file.type || "").toLowerCase();
      const isImageType = allowedImageTypes.includes(mimeType);
      const isFileType = allowedFileTypes.includes(mimeType);
      if (!isImageType && !isFileType) {
        setAttachError(
          "Unsupported file type. Use PDF, TXT, CSV, PNG, JPG, or WEBP."
        );
        return;
      }
      if (isImageType && !canAttachImage) {
        setAttachError("Image uploads are not enabled on this server.");
        return;
      }
      if (isFileType && !canAttachFile) {
        setAttachError("File uploads are not enabled on this server.");
        return;
      }
      event.preventDefault();
      attachFile(file);
    },
    [
      attachmentControlsDisabled,
      attachmentsAvailable,
      allowedImageTypes,
      allowedFileTypes,
      canAttachImage,
      canAttachFile,
      attachFile,
    ]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (accessGate) return;
    const handleDocumentPaste = (event) => {
      handlePaste(event);
    };
    document.addEventListener("paste", handleDocumentPaste);
    return () => {
      document.removeEventListener("paste", handleDocumentPaste);
    };
  }, [accessGate, isOpen, handlePaste]);

  useEffect(() => {
    if (!isOpen) return;
    if (accessGate) {
      setCapabilities(null);
      setCapabilitiesLoading(false);
      setHealthLoading(false);
      setError(accessGate.message);
      return;
    }
    if (!salonId) {
      setCapabilities(null);
      setError("Select a salon to use AI Assistant.");
      setCapabilitiesLoading(false);
      return;
    }
    let isActive = true;
    setAiConfigured(false);
    setHealthLoading(true);
    getAiHealth()
      .then((data) => {
        if (!isActive) return;
        const providerOk = data?.ok === true;
        const legacyConfigured = data?.configured === true;
        const legacyInitOk = data?.init_ok !== false;
        const configured = Boolean(providerOk || (legacyConfigured && legacyInitOk));
        setAiConfigured(configured);
        if (!configured) {
          if (legacyConfigured && !legacyInitOk) {
            setError("AI Assistant is not ready on this server.");
          } else {
            setError(AI_TEMPORARILY_UNAVAILABLE_MESSAGE);
          }
        }
      })
      .catch(() => {
        if (!isActive) return;
        setAiConfigured(false);
        setError(AI_TEMPORARILY_UNAVAILABLE_MESSAGE);
      })
      .finally(() => {
        if (!isActive) return;
        setHealthLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [accessGate, isOpen, salonId]);

  useEffect(() => {
    if (!isOpen || !aiConfigured || accessGate) return;
    if (!salonId) {
      setCapabilities(null);
      setError("Select a salon to use AI Assistant.");
      setCapabilitiesLoading(false);
      return;
    }
    let isActive = true;
    setCapabilitiesLoading(true);
    setError("");
    getAiCapabilities({ salonId })
      .then((data) => {
        if (!isActive) return;
        setCapabilities(data || null);
      })
      .catch((err) => {
        if (!isActive) return;
        const status = err?.response?.status;
        if (status === 403) {
          setError("Unable to access this salon. Please check your salon context.");
        } else if (status === 400) {
          setError(
            err?.response?.data?.detail ||
              "Unable to load AI Assistant capabilities."
          );
        } else {
          setError("Unable to load AI Assistant capabilities.");
        }
        setCapabilities(null);
      })
      .finally(() => {
        if (!isActive) return;
        setCapabilitiesLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [accessGate, isOpen, salonId, aiConfigured]);

  const handleActions = (nextActions) => {
    if (!Array.isArray(nextActions) || nextActions.length === 0) return;
    const nav = nextActions.find(
      (action) => action?.type === "NAVIGATE" && action?.path
    );
    if (!nav?.path) return;
    const normalizedPath = normalizeLegacySchedulePath(nav.path);
    const page = nav?.page || inferPageFromPath(normalizedPath);
    const policy = page ? NAV_PAGE_POLICIES[page] : null;
    const normalizedRole = normalizeRole(user?.role);
    const hasRole =
      policy?.allowedRoles?.length
        ? policy.allowedRoles.includes(normalizedRole)
        : false;
    const hasPerm = policy ? isPolicyAllowedByPermissions(permissions, policy) : false;
    const pathOk = page ? isPathAllowedForPage(page, normalizedPath) : false;
    if (!policy || !hasRole || !hasPerm || !pathOk) {
      setError("You don't have permission to open that page.");
      return;
    }
    navigate(normalizedPath);
    if (onClose) onClose();
    return true;
  };

  const filterSupportActions = useCallback((actions) => {
    if (!Array.isArray(actions)) return [];
    return actions.filter((action) => {
      const type = String(action?.type || "");
      if (!SUPPORT_ACTION_ALLOWLIST.has(type)) return false;
      if (type === "navigate") {
        const target = String(action?.to || "");
        return SUPPORT_ROUTE_ALLOWLIST.includes(target);
      }
      if (type === "open_modal") {
        return action?.id === "newAppointment";
      }
      if (type === "highlight") {
        return SUPPORT_SELECTOR_ALLOWLIST.includes(action?.selector);
      }
      if (type === "prefill") {
        return SUPPORT_PREFILL_ALLOWLIST.has(action?.field);
      }
      return false;
    });
  }, []);

  const executeSupportAction = useCallback(
    (action) => {
      if (!action) return false;
      setSupportNotice("");
      const type = String(action.type || "");
      if (!SUPPORT_ACTION_ALLOWLIST.has(type)) return false;
      if (type === "navigate") {
        const target = String(action.to || "");
        if (!SUPPORT_ROUTE_ALLOWLIST.includes(target)) {
          return false;
        }
        navigate(target);
        return true;
      }
      const scheduleContainer = document.querySelector(
        "[data-testid='manager-schedule-page']"
      );
      if (type === "open_modal") {
        if (action.id !== "newAppointment") return false;
        if (!scheduleContainer) {
          setSupportNotice("Please open Schedule first.");
          return false;
        }
        const trigger = document.querySelector(
          "[data-testid='new-appointment-btn']"
        );
        if (!trigger) {
          setSupportNotice(
            "I couldn't open that window—please click New Appointment."
          );
          return false;
        }
        if (trigger.disabled) {
          setSupportNotice(
            "New Appointment is disabled. Ask your manager if you need access."
          );
          return false;
        }
        trigger.click();
        return true;
      }
      if (type === "highlight") {
        const selector = String(action.selector || "");
        if (!SUPPORT_SELECTOR_ALLOWLIST.includes(selector)) return false;
        const target = document.querySelector(selector);
        if (!scheduleContainer || !target || !scheduleContainer.contains(target)) {
          const label = labelForSupportAction(action);
          setSupportNotice(
            `I couldn't highlight that button—please look for: ${label}.`
          );
          return false;
        }
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.classList.add("sbin-support-highlight");
        window.setTimeout(() => {
          target.classList.remove("sbin-support-highlight");
        }, 3500);
        return true;
      }
      if (type === "prefill") {
        const field = String(action.field || "");
        if (!SUPPORT_PREFILL_ALLOWLIST.has(field)) return false;
        if (!scheduleContainer && field !== "clientSearch") {
          setSupportNotice("Please open Schedule first.");
          return false;
        }
        let input = null;
        if (field === "clientSearch") {
          input = document.querySelector("[data-testid='client-search-input']");
        } else if (field === "date") {
          input = scheduleContainer
            ? scheduleContainer.querySelector("input[type='date']")
            : null;
        } else if (field === "time") {
          input = scheduleContainer
            ? scheduleContainer.querySelector("input[type='time']")
            : null;
        }
        const nextValue = String(action.value || "");
        if (!input || !setInputValue(input, nextValue)) {
          setSupportNotice("I couldn't prefill that field. Please enter it manually.");
          return false;
        }
        return true;
      }
      return false;
    },
    [navigate]
  );

  const handleAppointmentNavigate = (path) => {
    if (!path) return;
    handleActions([{ type: "NAVIGATE", page: "MANAGER_SCHEDULE", path }]);
  };

  const handleToolResults = (results, options = {}) => {
    if (!Array.isArray(results) || results.length === 0) return;
    if (options.skipNavigation) return;
    const clarification = results.find(
      (result) =>
        result?.output?.status === "needs_clarification" ||
        result?.output?.status === "ambiguous"
    );
    if (clarification?.output?.question) {
      const question = clarification.output.question;
      const quickRepliesOverride = Array.isArray(clarification.output?.quick_replies)
        ? clarification.output.quick_replies
            .map((reply) =>
              reply?.label && reply?.message ? reply : null
            )
            .filter(Boolean)
        : [];
      const clarificationPayload = clarification.output.clarification || null;
      const pickOptions = Array.isArray(clarificationPayload?.options)
        ? clarificationPayload.options
        : [];
      const isClientPick =
        clarificationPayload?.type === "pick_client" && pickOptions.length > 0;
      const isAppointmentPick =
        clarificationPayload?.type === "pick_appointment" && pickOptions.length > 0;
      if (isClientPick) {
        const normalizedOptions = pickOptions
          .map((option) => {
            const index = Number(option?.index);
            const clientId = option?.client_id;
            if (!index || !clientId) return null;
            return {
              index,
              client_id: clientId,
              label: option?.label || `Option ${index}`,
            };
          })
          .filter(Boolean);
        setPendingClientPick(normalizedOptions);
        setPendingAppointmentPick(null);
        setMessages((prev) => {
          if (
            prev.length > 0 &&
            prev[prev.length - 1]?.role === "assistant" &&
            prev[prev.length - 1]?.content === question
          ) {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              pickOptions: normalizedOptions,
            };
            return next;
          }
          return [
            ...prev,
            {
              role: "assistant",
              content: question,
              pickOptions: normalizedOptions,
            },
          ];
        });
      } else if (isAppointmentPick) {
        const normalizedOptions = pickOptions
          .map((option) => {
            const index = Number(option?.index);
            const appointmentId = option?.appointment_id;
            if (!index || !appointmentId) return null;
            return {
              index,
              appointment_id: appointmentId,
              label: option?.label || `Option ${index}`,
            };
          })
          .filter(Boolean);
        setPendingAppointmentPick(normalizedOptions);
        setPendingClientPick(null);
        setPendingCancelAction(null);
        setMessages((prev) => {
          if (
            prev.length > 0 &&
            prev[prev.length - 1]?.role === "assistant" &&
            prev[prev.length - 1]?.content === question
          ) {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              appointmentPickOptions: normalizedOptions,
            };
            return next;
          }
          return [
            ...prev,
            {
              role: "assistant",
              content: question,
              appointmentPickOptions: normalizedOptions,
            },
          ];
        });
      } else {
        const candidates = Array.isArray(clarification.output.candidates)
          ? clarification.output.candidates
          : [];
        const quickReplies =
          quickRepliesOverride.length > 0
            ? quickRepliesOverride
            : candidates.map((candidate, index) => ({
                label: buildCandidateLabel(candidate, index),
                message: buildCandidateMessage(candidate),
              }));
        setPendingClientPick(null);
        setPendingAppointmentPick(null);
        setMessages((prev) => {
          if (
            prev.length > 0 &&
            prev[prev.length - 1]?.role === "assistant" &&
            prev[prev.length - 1]?.content === question
          ) {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              quickReplies,
            };
            return next;
          }
          return [
            ...prev,
            {
              role: "assistant",
              content: question,
              quickReplies,
            },
          ];
        });
      }
      return;
    }
    const serviceResult = results.find(
      (result) =>
        result?.name === "search_services" && result?.output?.status === "success"
    );
    if (serviceResult?.output?.status === "success") {
      const output = serviceResult.output || {};
      const serviceResults = Array.isArray(output.results) ? output.results : [];
      const fieldsUsed = Array.isArray(output.fields_used) ? output.fields_used : [];
      const countValue = Number(output.count || serviceResults.length || 0);
      const summary =
        output.message_for_ai ||
        `Found ${serviceResults.length} services matching "${output.query_used || ""}".`;
      const note =
        countValue > 25
          ? "Tell me another keyword or say 'next' to see more."
          : "";
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1]?.role === "assistant") {
          const next = [...prev];
          next[next.length - 1] = {
            ...next[next.length - 1],
            content: next[next.length - 1]?.content || summary,
            serviceResults,
            serviceFieldsUsed: fieldsUsed,
            serviceNote: note,
          };
          return next;
        }
        return [
          ...prev,
          {
            role: "assistant",
            content: summary,
            serviceResults,
            serviceFieldsUsed: fieldsUsed,
            serviceNote: note,
          },
        ];
      });
      return;
    }
    const listResult = results.find(
      (result) =>
        result?.name === "list_scheduled_appointments" &&
        result?.output?.status === "success"
    );
    if (listResult?.output?.status === "success") {
      const output = listResult.output || {};
      const appointments = Array.isArray(output.results) ? output.results : [];
      const listMeta = {
        range: output.range_used || null,
        status_filter: output.status_filter_used || "scheduled",
        include_deleted: Boolean(output.include_deleted),
        limit: output.limit_used || output.count || 200,
        offset: output.offset_used || 0,
        sort: output.sort_used || "start_asc",
        fields: Array.isArray(output.fields_used) ? output.fields_used : null,
      };
      const nextOffset = output.next_offset ?? null;
      const summary =
        output.message_for_ai ||
        `Showing ${appointments.length} appointments.`;
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1]?.role === "assistant") {
          const next = [...prev];
          next[next.length - 1] = {
            ...next[next.length - 1],
            content: next[next.length - 1]?.content || summary,
            appointmentList: appointments,
            appointmentListMeta: listMeta,
            appointmentListNextOffset: nextOffset,
            appointmentListTotal: output.total ?? null,
          };
          return next;
        }
        return [
          ...prev,
          {
            role: "assistant",
            content: summary,
            appointmentList: appointments,
            appointmentListMeta: listMeta,
            appointmentListNextOffset: nextOffset,
            appointmentListTotal: output.total ?? null,
          },
        ];
      });
      return;
    }
    const appointmentResult = results.find(
      (result) =>
        result?.name === "find_appointments" && result?.output?.status === "found"
    );
    if (appointmentResult?.output?.status === "found") {
      const bestMatch = appointmentResult.output?.best_match || null;
      const appointmentId = bestMatch?.appointment_id;
      if (appointmentId) {
        setPendingCancelAction({ appointment_id: appointmentId });
        setPendingRescheduleAction({ appointment_id: appointmentId });
        setPendingAppointmentPick(null);
        setMessages((prev) => {
          if (prev.length > 0 && prev[prev.length - 1]?.role === "assistant") {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              cancelActionOptions: [
                { label: "Mark as No-Show", action: "no_show" },
                { label: "Delete", action: "delete" },
              ],
              rescheduleActionOptions: [{ label: "Reschedule", action: "reschedule" }],
              cancelAppointmentId: appointmentId,
              rescheduleAppointmentId: appointmentId,
            };
            return next;
          }
          return [
            ...prev,
            {
              role: "assistant",
              content:
                "Do you want to reschedule it, mark it as No-Show, or Delete?",
              cancelActionOptions: [
                { label: "Mark as No-Show", action: "no_show" },
                { label: "Delete", action: "delete" },
              ],
              rescheduleActionOptions: [{ label: "Reschedule", action: "reschedule" }],
              cancelAppointmentId: appointmentId,
              rescheduleAppointmentId: appointmentId,
            },
          ];
        });
      }
      return;
    }
    const cancelResult = results.find(
      (result) =>
        result?.name === "cancel_appointment" && result?.output?.status === "success"
    );
    if (cancelResult?.output?.status === "success") {
      setPendingCancelAction(null);
      setPendingRescheduleAction(null);
      setPendingAppointmentPick(null);
    }
    const rescheduleResult = results.find(
      (result) => result?.name === "reschedule_appointment"
    );
    if (rescheduleResult) {
      const output = rescheduleResult.output || {};
      if (output?.status === "success") {
        setPendingRescheduleAction(null);
        setPendingCancelAction(null);
        setPendingAppointmentPick(null);
        const startLabel =
          output.start_time_local || output.start_local_display || "";
        const endLabel =
          output.end_time_local || output.end_local_display || "";
        const summary =
          output.message ||
          (startLabel
            ? `Appointment moved to: ${startLabel}${endLabel ? ` - ${endLabel}` : ""}`
            : "Appointment rescheduled.");
        const rescheduleSummary = {
          startLabel,
          endLabel,
          employeeName: output.employee_name || "",
          navigation: output.navigation || null,
        };
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: summary,
            rescheduleSummary,
          },
        ]);
        return;
      }
      if (output?.message) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: output.message },
        ]);
        return;
      }
    }
    for (const result of results) {
      if (result?.name !== "open_client_edit") continue;
      const output = result?.output;
      if (output?.error === "PERMISSION_DENIED") {
        setError(noClientEditPermissionMessage);
        return;
      }
      if (output?.error === "CLIENT_NOT_FOUND") {
        setError("Client not found.");
        return;
      }
      if (output?.error === "CLIENT_ID_REQUIRED") {
        setError("Select a client before opening the edit page.");
        return;
      }
    }
    for (const result of results) {
      if (result?.name !== "open_page") continue;
      const output = result?.output;
      if (output?.error === "PERMISSION_DENIED") {
        setError("You don't have permission to open that page.");
        return;
      }
    }
    for (const result of results) {
      if (result?.name !== "book_appointment") continue;
      const output = result?.output;
      if (output?.error === "PERMISSION_DENIED") {
        setError(output?.message || "You don't have permission to book appointments.");
        return;
      }
    }
    const navResult = results.find(
      (result) => result?.name === "book_appointment" && result?.output?.navigation
    );
    if (navResult?.output?.navigation) {
      const nav = navResult.output.navigation;
      const rawPath = nav?.path || nav?.route;
      if (typeof rawPath !== "string" || !rawPath) return;
      const normalizedPath = normalizeLegacySchedulePath(rawPath);
      const page = inferPageFromPath(normalizedPath);
      const policy = page ? NAV_PAGE_POLICIES[page] : null;
      const normalizedRole = normalizeRole(user?.role);
      const hasRole =
        policy?.allowedRoles?.length
          ? policy.allowedRoles.includes(normalizedRole)
          : false;
      const hasPerm = policy ? isPolicyAllowedByPermissions(permissions, policy) : false;
      const pathOk = page ? isPathAllowedForPage(page, normalizedPath) : false;
      if (!policy || !hasRole || !hasPerm || !pathOk) {
        setError("You don't have permission to open that page.");
        return;
      }
      navigate(normalizedPath);
      if (onClose) onClose();
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (table, format) => {
    const token = table?.export_token;
    if (!token || !salonId) return;
    setExportState((prev) => ({
      ...prev,
      [token]: {
        ...(prev[token] || {}),
        loading: true,
        error: "",
      },
    }));
    try {
      const res = await exportAiTable({
        salonId,
        exportToken: token,
        format,
      });
      if (res.status === 200) {
        const filename = parseFilename(res.headers?.["content-disposition"]);
        downloadBlob(res.data, filename);
        setExportState((prev) => ({
          ...prev,
          [token]: { ...(prev[token] || {}), loading: false },
        }));
        return;
      }
      const payload = await blobToJson(res.data);
      if (res.status === 409 && payload?.error === "pdf_not_supported") {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            error: "PDF export is not available for this report.",
          },
        }));
        return;
      }
      if (res.status === 409 && payload?.error === "pdf_unavailable") {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            showZip: true,
            error: "PDF export engine not available. Use CSV or ZIP.",
          },
        }));
        return;
      }
      if (res.status === 409 && payload?.error === "csv_not_supported") {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            error: "CSV export is not available for this report.",
          },
        }));
        return;
      }
      if (res.status === 409 && payload?.error === "too_large") {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            showZip: true,
            error:
              "This export is too large for CSV/PDF. Download ZIP instead.",
          },
        }));
        return;
      }
      if (res.status === 403) {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            error: "You don't have permission to export this report.",
          },
        }));
        return;
      }
      if (res.status === 413) {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            error: "Export is too large. Narrow the date range and try again.",
          },
        }));
        return;
      }
      if (res.status === 404) {
        setExportState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] || {}),
            loading: false,
            error: "Export expired. Please rerun the report.",
          },
        }));
        return;
      }
      setExportState((prev) => ({
        ...prev,
        [token]: {
          ...(prev[token] || {}),
          loading: false,
          error:
            payload?.detail ||
            "Export failed. Please try again.",
        },
      }));
    } catch (err) {
      setExportState((prev) => ({
        ...prev,
        [token]: {
          ...(prev[token] || {}),
          loading: false,
          error: "Export failed. Please try again.",
        },
      }));
    }
  };

  const handleSend = async (
    content,
    contextOverride = null,
    supportOverride = null
  ) => {
    const trimmed = (content || "").trim();
    const hasAttachment = Boolean(attachedImage || attachedFile);
    if ((!trimmed && !hasAttachment) || isLoading) return;
    const effectiveContextOverride = contextOverride || nextContextOverride;
    if (contextOverride || nextContextOverride) {
      setNextContextOverride(null);
    }
    let outgoing = trimmed;
    let cancelContext = pendingCancelAction;
    let rescheduleContext = pendingRescheduleAction;
    const routeAppointmentId = routeAppointmentContext.current_appointment_id;
    if (
      routeAppointmentId &&
      /(?:reschedule|move|change)/i.test(trimmed) &&
      /\b(?:that|this|current|highlighted|selected)\s+appointment\b/i.test(trimmed) &&
      !/\b(?:appointment|appt)\s*#?\s*\d+\b/i.test(trimmed)
    ) {
      rescheduleContext = { appointment_id: routeAppointmentId };
      setPendingRescheduleAction(rescheduleContext);
      outgoing = `Reschedule appointment ${routeAppointmentId} to ${trimmed}`;
    }
    if (pendingAppointmentPick && /^\d+$/.test(trimmed)) {
      const index = Number(trimmed);
      const match = Array.isArray(pendingAppointmentPick)
        ? pendingAppointmentPick.find((option) => Number(option.index) === index)
        : null;
      if (match?.appointment_id) {
        outgoing = `Selected appointment: ${match.appointment_id}`;
        setPendingAppointmentPick(null);
        cancelContext = { appointment_id: match.appointment_id };
        setPendingCancelAction(cancelContext);
        rescheduleContext = { appointment_id: match.appointment_id };
        setPendingRescheduleAction(rescheduleContext);
      }
    } else if (pendingClientPick && /^\d+$/.test(trimmed)) {
      const index = Number(trimmed);
      const match = Array.isArray(pendingClientPick)
        ? pendingClientPick.find((option) => Number(option.index) === index)
        : null;
      if (match?.client_id) {
        outgoing = `Selected client: ${match.client_id}`;
        setPendingClientPick(null);
      }
    }
    if (/^Selected appointment:/i.test(trimmed)) {
      setPendingAppointmentPick(null);
      const match = /Selected appointment:\s*(\d+)/i.exec(trimmed);
      if (match?.[1]) {
        cancelContext = { appointment_id: Number(match[1]) };
        setPendingCancelAction(cancelContext);
        rescheduleContext = { appointment_id: Number(match[1]) };
        setPendingRescheduleAction(rescheduleContext);
      }
    }
    if (/^Selected client:/i.test(trimmed)) {
      setPendingClientPick(null);
    }
    if (
      pendingCancelAction &&
      /^(no[- ]?show|delete)$/i.test(trimmed)
    ) {
      const action = /^no[- ]?show$/i.test(trimmed) ? "no_show" : "delete";
      outgoing = `Cancel action: ${action} for appointment ${pendingCancelAction.appointment_id}`;
      setPendingCancelAction(null);
      cancelContext = null;
    } else if (/^Cancel action:/i.test(trimmed)) {
      setPendingCancelAction(null);
      cancelContext = null;
    }
    if (/^Reschedule appointment/i.test(trimmed)) {
      const match = /Reschedule appointment\s*(\d+)/i.exec(trimmed);
      if (match?.[1]) {
        rescheduleContext = { appointment_id: Number(match[1]) };
        setPendingRescheduleAction(rescheduleContext);
      }
    }
    if (
      rescheduleContext &&
      rescheduleContext.appointment_id &&
      !/reschedule/i.test(trimmed) &&
      !/appointment\s+\d+/i.test(trimmed)
    ) {
      const looksLikeTime = /\b\d{1,2}(:\d{2})?\s*(am|pm)?\b/i.test(trimmed);
      const looksLikeDate =
        /\b\d{4}-\d{2}-\d{2}\b/.test(trimmed) ||
        /(today|tomorrow|yesterday|same day|same-day)/i.test(trimmed);
      if (looksLikeTime || looksLikeDate) {
        outgoing = `Reschedule appointment ${rescheduleContext.appointment_id} to ${trimmed}`;
      }
    }
    if (
      pendingClientPick &&
      /(list|options|show me the list|show options|gimme list)/i.test(trimmed) &&
      !/^\d+$/.test(trimmed)
    ) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Here are the options:",
          pickOptions: pendingClientPick,
        },
      ]);
      return;
    }
    if (!outgoing && hasAttachment) {
      outgoing = attachedImage
        ? "Please review the attached image."
        : "Please review the attached file.";
    }
    if (accessGate) {
      setError(accessGate.message);
      return;
    }
    if (!salonId) {
      setError("Select a salon to use AI Assistant.");
      return;
    }
    if (!aiConfigured) {
      setError(AI_TEMPORARILY_UNAVAILABLE_MESSAGE);
      return;
    }
    setError("");
    setSupportNotice("");
    setIsLoading(true);
    let messageAttachment = null;
    if (attachedImage) {
      try {
        const previewUrl = await readFileAsDataUrl(attachedImage);
        messageAttachment = {
          kind: "image",
          name: attachedImage.name || "image",
          mime: attachedImage.type,
          size: attachedImage.size,
          previewUrl: previewUrl || null,
        };
      } catch (err) {
        messageAttachment = {
          kind: "image",
          name: attachedImage.name || "image",
          mime: attachedImage.type,
          size: attachedImage.size,
          previewUrl: null,
        };
      }
    } else if (attachedFile) {
      messageAttachment = {
        kind: "file",
        name: attachedFile.name || "file",
        mime: attachedFile.type,
        size: attachedFile.size,
      };
    }
    const userMessage = {
      role: "user",
      content: outgoing,
      ...(messageAttachment ? { attachment: messageAttachment } : {}),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    const apiMessages = nextMessages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
    let didSucceed = false;
    try {
      const supportModeValue = Boolean(supportOverride?.supportMode);
      const supportContextValue =
        supportOverride?.supportContext || supportContext;
      if (hasAttachment) {
      const response = await sendAiChatWithAttachments({
        salonId,
        messages: apiMessages,
        context: {
          timezone: effectiveTimezone || undefined,
          current_appointment_id: routeAppointmentContext.current_appointment_id,
          current_appointment_date: routeAppointmentContext.current_appointment_date,
          ...(effectiveContextOverride || {}),
        },
        supportContext: supportContextValue,
        supportMode: supportModeValue,
        imageFile: attachedImage || undefined,
        docFile: attachedFile || undefined,
        });
        const assistantText = response?.assistant_message || "Done.";
        const supportPayload = parseSupportPayload(assistantText);
        const attachmentFailed =
          hasAttachment &&
          /couldn'?t process that attachment|unable to process that attachment|attachment.*smaller|ask with text only/i.test(
            assistantText
          );
        const shouldAttachCancelActions =
          cancelContext &&
          /no[- ]?show/i.test(assistantText) &&
          /delete/i.test(assistantText);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: assistantText,
            support: supportPayload || null,
            ...(shouldAttachCancelActions
              ? {
                  cancelActionOptions: [
                    { label: "Mark as No-Show", action: "no_show" },
                    { label: "Delete", action: "delete" },
                  ],
                  cancelAppointmentId: cancelContext.appointment_id,
                }
              : {}),
            tables: Array.isArray(response?.tables) ? response.tables : null,
            export_ready: response?.export_ready || null,
            detailAvailable: Boolean(response?.detail_available),
          },
        ]);
        const didNavigate = Boolean(handleActions(response?.actions));
        handleToolResults(response?.tool_results, { skipNavigation: didNavigate });
        if (attachmentFailed) {
          setError(assistantText);
          didSucceed = false;
        } else {
          didSucceed = true;
        }
      } else {
        if (streamAbortRef.current) {
          streamAbortRef.current.abort();
        }
        const controller = new AbortController();
        streamAbortRef.current = controller;
        const streamId = Date.now();
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "",
            isStreaming: true,
            status: "routing",
            streamId,
          },
        ]);
        const donePayload = await streamAiChat({
          salonId,
          messages: apiMessages,
          context: {
            timezone: effectiveTimezone || undefined,
            current_appointment_id: routeAppointmentContext.current_appointment_id,
            current_appointment_date: routeAppointmentContext.current_appointment_date,
            ...(effectiveContextOverride || {}),
          },
          supportContext: supportContextValue,
          supportMode: supportModeValue,
          signal: controller.signal,
          onEvent: ({ event, data }) => {
            if (event === "status") {
              const statusValue =
                typeof data === "string" ? data : data?.status || data;
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.streamId === streamId) {
                  next[next.length - 1] = {
                    ...last,
                    status: statusValue || last.status,
                  };
                }
                return next;
              });
              return;
            }
            if (event === "delta") {
              const deltaText =
                typeof data === "string" ? data : data?.delta || "";
              if (!deltaText) return;
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.streamId === streamId) {
                  next[next.length - 1] = {
                    ...last,
                    content: `${last.content || ""}${deltaText}`,
                  };
                }
                return next;
              });
            }
            if (event === "error") {
              setError(formatAiError(data?.detail || data?.message || ""));
            }
          },
        });
        streamAbortRef.current = null;
        const assistantText =
          donePayload?.assistant_message ||
          (donePayload?.error ? "Something went wrong." : "Done.");
        const supportPayload = parseSupportPayload(assistantText);
        const shouldAttachCancelActions =
          cancelContext &&
          /no[- ]?show/i.test(assistantText) &&
          /delete/i.test(assistantText);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.streamId === streamId) {
            next[next.length - 1] = {
              ...last,
              role: "assistant",
              content: assistantText || last.content || "Done.",
              support: supportPayload || null,
              isStreaming: false,
              status: null,
              ...(shouldAttachCancelActions
                ? {
                    cancelActionOptions: [
                      { label: "Mark as No-Show", action: "no_show" },
                      { label: "Delete", action: "delete" },
                    ],
                    cancelAppointmentId: cancelContext?.appointment_id,
                  }
                : {}),
              tables: Array.isArray(donePayload?.tables) ? donePayload.tables : null,
              export_ready: donePayload?.export_ready || null,
              detailAvailable: Boolean(donePayload?.detail_available),
            };
          }
          return next;
        });
        if (donePayload?.actions) {
          const didNavigate = Boolean(handleActions(donePayload?.actions));
          handleToolResults(donePayload?.tool_results, { skipNavigation: didNavigate });
        } else {
          handleToolResults(donePayload?.tool_results, { skipNavigation: false });
        }
        if (donePayload?.error) {
          setError(formatAiError(donePayload?.error));
          didSucceed = false;
        } else {
          didSucceed = true;
        }
      }
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      const isDev = process.env.NODE_ENV !== "production";
      if (isDev && status >= 500) {
        console.error("AI Assistant error", err?.response?.data || err);
      }
      if (status === 429) {
        setError("Rate limit reached. Try again in a few minutes.");
      } else if (status === 403) {
        setError("Unable to access this salon. Please check your salon context.");
      } else if (status === 413) {
        setError(
          err?.response?.data?.detail ||
            err?.detail ||
            "Attachment is too large. Please try a smaller file."
        );
      } else if (status === 400) {
        setError(
          err?.response?.data?.detail ||
            err?.detail ||
            "Please check your request and try again."
        );
      } else {
        if (isDev && (err?.response?.data?.detail || err?.detail)) {
          setError(formatAiError(err?.response?.data?.detail || err?.detail));
        } else {
          setError(formatAiError(err?.response?.data?.detail || err?.detail));
        }
      }
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.isStreaming) {
          next[next.length - 1] = {
            ...last,
            content: last.content || "Something went wrong.",
            isStreaming: false,
            status: null,
          };
        }
        return next;
      });
    } finally {
      setIsLoading(false);
      if (didSucceed) {
        setInput("");
        if (hasAttachment) {
          clearAttachments();
        }
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSend(input);
  };

  const today = new Date();
  const salonTodayIso =
    parseIsoDate(capabilities?.salon_today_iso) ? capabilities.salon_today_iso : "";
  const last7End = salonTodayIso || formatDateISO(today, effectiveTimezone);
  const last7Start = salonTodayIso
    ? addDaysIso(salonTodayIso, -6)
    : formatDateISO(addDays(today, -6), effectiveTimezone);
  const monthStart = salonTodayIso
    ? monthStartIso(salonTodayIso)
    : formatDateISO(
        new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0),
        effectiveTimezone
      );

  const shortcutCategories = useMemo(() => {
    const categories = [];
    categories.push({
      id: "user_manual",
      label: "User Manual",
      title: "Ellyanna Booking user manual",
      subtitle: "Learn how to activate and use features in Ellyanna Booking.",
      actions: [
        {
          label: "Start here",
          message:
            "Give me a beginner-friendly overview of how to activate and use the main Ellyanna Booking features.",
          kind: "help",
          supportMode: true,
        },
        {
          label: "Setup checklist",
          message:
            "Walk me through the Ellyanna Booking setup checklist and what each setup area is used for.",
          kind: "help",
          supportMode: true,
        },
        {
          label: "Feature guide",
          message:
            "Explain the main Ellyanna Booking features and where to find them.",
          kind: "help",
          supportMode: true,
        },
        {
          label: "Permissions",
          message:
            "Explain which permissions turn on the main Ellyanna Booking features.",
          kind: "help",
          supportMode: true,
        },
      ],
    });

    const appointmentActions = [];
    if (canUseFullSchedule) {
      appointmentActions.push(
        {
          label: "Book appointment",
          message: "I want to book an appointment",
          kind: "action",
        },
        {
          label: "Reschedule appointment",
          message: "I want to reschedule an appointment",
          kind: "action",
        },
        {
          label: "Cancel appointment",
          message: "I want to cancel an appointment",
          kind: "action",
        },
        {
          label: "Find today",
          message: "List scheduled appointments today",
          kind: "lookup",
        },
        {
          label: "Booking steps",
          message: "How do I book an appointment?",
          kind: "help",
          supportMode: true,
        }
      );
    } else if (canViewSchedule) {
      appointmentActions.push({
        label: "Schedule help",
        message: "What schedule information can I access?",
        kind: "help",
      });
    }
    if (appointmentActions.length) {
      categories.push({
        id: "appointments",
        label: "Appointments",
        title: "Appointment tasks",
        subtitle: "Book, reschedule, cancel, or get booking steps.",
        actions: appointmentActions,
      });
    }

    const clientActions = [];
    if (canAccessClients) {
      clientActions.push(
        {
          label: canManageClients ? "Find or edit client" : "Find client",
          message: canManageClients
            ? "Help me find a client record"
            : "Help me look up a client contact",
          kind: "lookup",
        },
        {
          label: "Client notes",
          message: "Help me search client notes",
          kind: "lookup",
        },
        {
          label: "Client visits",
          message: "Explain client visit and spend information",
          kind: "help",
        }
      );
    }
    if (clientActions.length) {
      categories.push({
        id: "clients",
        label: "Clients",
        title: "Client tasks",
        subtitle: "Find clients, review notes, or understand client history.",
        actions: clientActions.slice(0, 5),
      });
    }

    const reportActions = [];
    if (canViewReports && currentPage === "REPORT_REWARDS_ANALYTICS") {
      reportActions.push(
        {
          label: "Rewards summary",
          message: "Rewards summary in the current range",
          context: { intent: "rewards_analytics" },
          kind: "report",
        },
        {
          label: "Top unused rewards",
          message: "Top unused rewards",
          context: { intent: "rewards_analytics" },
          kind: "report",
        }
      );
    }
    if (canViewReports && currentPage === "REPORT_TRAINING_PROGRESS") {
      reportActions.push({
        label: "Training progress",
        message: "Training progress summary last 30 days",
        context: { intent: "training_progress", date_preset: "last_30_days" },
        kind: "report",
      });
    }
    if (canViewReports) {
      reportActions.push(
        {
          label: "Sales last 7 days",
          message: "Sales summary last 7 days",
          context: { start_date: last7Start, end_date: last7End },
          kind: "report",
        },
        {
          label: "Sales this month",
          message: "Sales summary this month",
          context: { start_date: monthStart, end_date: last7End },
          kind: "report",
        },
        {
          label: "Top employee",
          message: "Top employee by revenue this month",
          context: {
            intent: "top_employees_by_revenue",
            top_n: 1,
            date_preset: "this_month",
          },
          kind: "report",
        },
        {
          label: "Refunds last 7 days",
          message: "Refunds summary last 7 days",
          context: { start_date: last7Start, end_date: last7End },
          kind: "report",
        }
      );
    }
    if (reportActions.length) {
      categories.push({
        id: "reports",
        label: "Reports",
        title: "Report questions",
        subtitle: "Ask for summaries, trends, and numbers in simple words.",
        actions: reportActions.slice(0, 5),
      });
    }

    const setupActions = [];
    if (canManageServices) {
      setupActions.push({
        label: "Services",
        message: "How do I manage services and products?",
        kind: "help",
        supportMode: true,
      });
    }
    if (canManageStaff) {
      setupActions.push({
        label: "Staff",
        message: "Help me manage staff",
        kind: "help",
      });
    }
    if (canManageSettings) {
      setupActions.push({
        label: "Salon setup",
        message: "How do I update Salon Setup?",
        kind: "help",
        supportMode: true,
      });
    }
    if (canManageUserRoles) {
      setupActions.push({
        label: "Permissions",
        message: "How do I update staff permissions?",
        kind: "help",
        supportMode: true,
      });
    }
    if (canManageTickets) {
      setupActions.push({
        label: "Tickets",
        message: "Help me with tickets and checkout",
        kind: "help",
      });
    }
    if (canAccessPayroll || canAccessTraining || canViewRewards) {
      setupActions.push({
        label: "More setup help",
        message: "What setup and management help can I ask for?",
        kind: "help",
      });
    }
    if (setupActions.length) {
      categories.push({
        id: "setup",
        label: "Setup Help",
        title: "Setup and management help",
        subtitle: "Get simple steps for salon settings and daily operations.",
        actions: setupActions.slice(0, 5),
      });
    }

    return categories;
  }, [
    canAccessClients,
    canAccessPayroll,
    canAccessTraining,
    canManageClients,
    canManageServices,
    canManageSettings,
    canManageStaff,
    canManageTickets,
    canManageUserRoles,
    canUseFullSchedule,
    canViewReports,
    canViewRewards,
    canViewSchedule,
    currentPage,
    last7Start,
    last7End,
    monthStart,
  ]);

  useEffect(() => {
    if (!isOpen || shortcutCategories.length === 0) return;
    const categoryIds = shortcutCategories.map((category) => category.id);
    setActiveShortcutCategory((current) => {
      if (categoryIds.includes(current)) return current;
      if (categoryIds.includes(defaultShortcutCategory)) return defaultShortcutCategory;
      return categoryIds[0];
    });
  }, [
    defaultShortcutCategory,
    isOpen,
    shortcutCategories,
  ]);

  const activeShortcutGroup = useMemo(
    () =>
      shortcutCategories.find((category) => category.id === activeShortcutCategory) ||
      shortcutCategories[0] ||
      null,
    [activeShortcutCategory, shortcutCategories]
  );

  const renderSupportCard = (payload) => {
    if (!payload) return null;
    const steps = Array.isArray(payload.steps) ? payload.steps : [];
    const quickSummary = Array.isArray(payload.quick_summary)
      ? payload.quick_summary
      : [];
    const mistakes = Array.isArray(payload.common_mistakes)
      ? payload.common_mistakes
      : [];
    const shortcuts = Array.isArray(payload.shortcuts) ? payload.shortcuts : [];
    const followUps = Array.isArray(payload.follow_up_questions)
      ? payload.follow_up_questions
      : [];
    const actions = filterSupportActions(payload.ui_actions);

    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
        <div className="space-y-1">
          <div className="text-base font-semibold text-slate-900">
            {payload.title || "Support"}
          </div>
          {quickSummary.length > 0 && (
            <ul className="list-disc pl-4 text-sm text-slate-600 space-y-1">
              {quickSummary.map((item, idx) => (
                <li key={`summary-${idx}`}>{item}</li>
              ))}
            </ul>
          )}
        </div>

        {steps.length > 0 && (
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <div
                key={`step-${step.step || idx}`}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3"
              >
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-rose-500 text-white text-xs font-semibold flex items-center justify-center">
                    {step.step || idx + 1}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="text-sm font-semibold text-slate-900">
                      {step.label}
                    </div>
                    <div className="text-sm text-slate-600">{step.details}</div>
                    {step.tip && (
                      <div className="text-xs text-rose-700">{`Tip: ${step.tip}`}</div>
                    )}
                    {step.where && (
                      <div className="text-xs text-slate-500">{step.where}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {mistakes.length > 0 && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">
              Common mistakes
            </div>
            <ul className="list-disc pl-4 space-y-1">
              {mistakes.map((item, idx) => (
                <li key={`mistake-${idx}`}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {shortcuts.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-500">
              Shortcuts
            </div>
            <ul className="list-disc pl-4 space-y-1">
              {shortcuts.map((item, idx) => (
                <li key={`shortcut-${idx}`}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {actions.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Try this now
            </div>
            <div className="flex flex-wrap gap-2">
              {actions.map((action, idx) => (
                <button
                  key={`support-action-${idx}`}
                  type="button"
                  onClick={() => executeSupportAction(action)}
                  className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                >
                  {labelForSupportAction(action)}
                </button>
              ))}
            </div>
            {supportNotice && (
              <div className="text-xs text-slate-500">{supportNotice}</div>
            )}
          </div>
        )}

        {followUps.length > 0 && (
          <div className="text-sm text-slate-600 space-y-1">
            {followUps.map((item, idx) => (
              <div key={`followup-${idx}`}>{item}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <AIAssistantHeaderIcon />
          <span>Ask Ellyanna - AI Assistant</span>
        </span>
      }
      size="lg"
      fitContent={false}
      panelClassName="overflow-hidden h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] rounded-2xl sm:rounded-3xl sm:h-[90vh] sm:max-h-[90vh]"
      overlayClassName="items-start px-2 py-[calc(8px+env(safe-area-inset-top,0px))] sm:items-center sm:px-4 sm:py-6"
      headerClassName="px-4 py-3 sm:px-6 sm:py-4"
      bodyClassName="px-4 py-4 sm:px-6 sm:py-6"
    >
      <div className="flex h-full flex-col gap-3 sm:gap-4">
        {shortcutCategories.length > 0 && activeShortcutGroup && (
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
            <div className="flex flex-wrap gap-2">
              {shortcutCategories.map((category) => {
                const isActive = category.id === activeShortcutGroup.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveShortcutCategory(category.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {activeShortcutGroup.title}
                </div>
                <div className="text-xs text-slate-500">
                  {activeShortcutGroup.subtitle}
                </div>
              </div>
              <div className="text-xs font-medium text-slate-500">
                Answers and allowed actions only
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {activeShortcutGroup.actions.map((action) => {
                const isAction = action.kind === "action";
                return (
                  <button
                    key={action.label}
                    type="button"
                    onClick={() =>
                      handleSend(
                        action.message,
                        action.context,
                        action.supportMode ? { supportMode: true } : null
                      )
                    }
                    disabled={isLoading || capabilitiesLoading}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-60 ${
                      isAction
                        ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`relative flex min-h-0 flex-1 flex-col gap-3 sm:gap-4 ${
            isDragActive ? "rounded-2xl ring-2 ring-rose-200" : ""
          }`}
        >
          {isDragActive && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/80 text-sm font-semibold text-rose-600">
              {ATTACHMENT_DROP_HINT}
            </div>
          )}
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4 space-y-3"
          >
            {messages.map((msg, index) => {
            const isUser = msg.role === "user";
            const attachment = msg.attachment || null;
            const attachmentLabel =
              attachment?.kind === "file" && /pdf/i.test(attachment?.mime || "")
                ? "PDF"
                : attachment?.kind === "image"
                ? "Image"
                : "File";
            if (!isUser && msg.support) {
              return (
                <div
                  key={`${msg.role}-${index}`}
                  className="flex justify-start"
                >
                  <div className="max-w-[90%]">
                    {renderSupportCard(msg.support)}
                  </div>
                </div>
              );
            }
            return (
              <div
                key={`${msg.role}-${index}`}
                className={`flex ${isUser ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed shadow-sm ${
                    isUser
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 border border-slate-200"
                  } whitespace-pre-line`}
                >
                  {msg.content}
                  {!isUser && msg.isStreaming && msg.status === "fetching_data" && (
                    <div className="mt-2 space-y-2">
                      <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
                      <div className="h-3 w-full animate-pulse rounded bg-slate-200" />
                      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200" />
                    </div>
                  )}
                  {!isUser && msg.status && (
                    <div className="mt-2 text-xs text-slate-400">
                      {STREAM_STATUS_LABELS[msg.status] || msg.status}
                    </div>
                  )}
                  {attachment && (
                    <div className="mt-2">
                      {attachment.kind === "image" && attachment.previewUrl ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewImageUrl(attachment.previewUrl);
                            setPreviewImageName(attachment.name || "Image");
                          }}
                          className="block"
                        >
                          <img
                            src={attachment.previewUrl}
                            alt={attachment.name || "Attached image"}
                            className="h-20 w-20 rounded-lg object-cover shadow-sm"
                          />
                        </button>
                      ) : (
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                          <span className="font-semibold">{attachmentLabel}</span>
                          <span>{attachment.name || "Attachment"}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {!isUser &&
                    Array.isArray(msg.pickOptions) &&
                    msg.pickOptions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.pickOptions.map((option) => (
                          <button
                            key={`pick-${option.index}-${option.client_id}`}
                            type="button"
                            onClick={() => handleSend(`Selected client: ${option.client_id}`)}
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {option.label ? `${option.index}. ${option.label}` : String(option.index)}
                          </button>
                        ))}
                      </div>
                    )}
                  {!isUser &&
                    Array.isArray(msg.appointmentPickOptions) &&
                    msg.appointmentPickOptions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.appointmentPickOptions.map((option) => (
                          <button
                            key={`appt-pick-${option.index}-${option.appointment_id}`}
                            type="button"
                            onClick={() =>
                              handleSend(`Selected appointment: ${option.appointment_id}`)
                            }
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {option.label ? `${option.index}. ${option.label}` : String(option.index)}
                          </button>
                        ))}
                      </div>
                    )}
                  {!isUser &&
                    Array.isArray(msg.quickReplies) &&
                    msg.quickReplies.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.quickReplies.map((reply, replyIndex) => (
                          <button
                            key={`${reply.label}-${replyIndex}`}
                            type="button"
                            onClick={() => handleSend(reply.message)}
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {reply.label}
                          </button>
                        ))}
                      </div>
                    )}
                  {!isUser && !msg.isStreaming && msg.detailAvailable && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => handleSend("Show details")}
                        disabled={isLoading || capabilitiesLoading}
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        Show details
                      </button>
                    </div>
                  )}
                  {!isUser &&
                    Array.isArray(msg.cancelActionOptions) &&
                    msg.cancelActionOptions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.cancelActionOptions.map((option) => (
                          <button
                            key={`cancel-${option.action}-${msg.cancelAppointmentId || "appt"}`}
                            type="button"
                            onClick={() =>
                              handleSend(
                                `Cancel action: ${option.action} for appointment ${msg.cancelAppointmentId}`
                              )
                            }
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  {!isUser &&
                    Array.isArray(msg.rescheduleActionOptions) &&
                    msg.rescheduleActionOptions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.rescheduleActionOptions.map((option) => (
                          <button
                            key={`reschedule-${option.action}-${msg.rescheduleAppointmentId || "appt"}`}
                            type="button"
                            onClick={() =>
                              handleSend(
                                `Reschedule appointment ${msg.rescheduleAppointmentId}`
                              )
                            }
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  {!isUser && msg.rescheduleSummary && (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-xs text-slate-700">
                      <div className="font-semibold text-slate-800">
                        Appointment moved
                      </div>
                      <div className="text-slate-600">
                        {(msg.rescheduleSummary.startLabel || "Time updated") +
                          (msg.rescheduleSummary.endLabel
                            ? ` - ${msg.rescheduleSummary.endLabel}`
                            : "")}
                      </div>
                      {msg.rescheduleSummary.employeeName ? (
                        <div className="text-slate-600">
                          Technician: {msg.rescheduleSummary.employeeName}
                        </div>
                      ) : null}
                      {msg.rescheduleSummary.navigation?.path ? (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleAppointmentNavigate(
                                msg.rescheduleSummary.navigation.path
                              )
                            }
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            View on Schedule
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {!isUser &&
                    Array.isArray(msg.serviceResults) &&
                    msg.serviceResults.length > 0 && (
                      <div className="mt-2">
                        <ul className="space-y-1">
                          {msg.serviceResults.slice(0, 25).map((service, idx) => (
                            <li key={`${service?.id || "service"}-${idx}`} className="text-slate-600">
                              - {buildServiceLine(service, msg.serviceFieldsUsed || [])}
                            </li>
                          ))}
                        </ul>
                        {msg.serviceNote ? (
                          <div className="mt-2 text-xs text-slate-500">{msg.serviceNote}</div>
                        ) : null}
                      </div>
                    )}
                  {!isUser &&
                    Array.isArray(msg.appointmentList) &&
                    msg.appointmentList.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {msg.appointmentList.map((appt, idx) => {
                          const dateLabel = appt?.date_local || "";
                          const timeLabel = appt?.start_local_display || "";
                          const left = [dateLabel, timeLabel].filter(Boolean).join(" - ");
                          const middle = `${appt?.client_name || "Client"} - ${
                            appt?.service_name || "Service"
                          }`;
                          const right = appt?.staff_name || "";
                          return (
                            <button
                              key={`${appt?.id || "appt"}-${idx}`}
                              type="button"
                              onClick={() => handleAppointmentNavigate(appt?.navigation?.path)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                            >
                              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                                <div className="text-slate-500">{left || "Appointment"}</div>
                                <div className="md:flex-1 md:px-3">{middle}</div>
                                <div className="text-slate-500">{right}</div>
                              </div>
                            </button>
                          );
                        })}
                        {msg.appointmentListNextOffset !== null &&
                        msg.appointmentListNextOffset !== undefined ? (
                          <button
                            type="button"
                            onClick={() => {
                              const range = msg.appointmentListMeta?.range;
                              const from = range?.from_local;
                              const to = range?.to_local;
                              const offset = msg.appointmentListNextOffset;
                              const status = msg.appointmentListMeta?.status_filter || "scheduled";
                              const textParts = [
                                "Load more scheduled appointments",
                                from && to ? `from ${from} to ${to}` : null,
                                status ? `status ${status}` : null,
                                `offset ${offset}`,
                              ].filter(Boolean);
                              handleSend(textParts.join(" "));
                            }}
                            disabled={isLoading || capabilitiesLoading}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Load more
                          </button>
                        ) : null}
                      </div>
                    )}
                  {!isUser && Array.isArray(msg.tables) && msg.tables.length > 0 && (
                    <div className="mt-3 space-y-4">
                      {msg.tables.map((table, tableIndex) => {
                        const exportToken = table?.export_token;
                        const exportInfo = exportToken ? exportState[exportToken] || {} : {};
                        const rowCount = Number(table?.rowCount || 0);
                        const tooLargeForCsv = rowCount > MAX_CSV_ROWS;
                        const tooLargeForPdf = rowCount > MAX_PDF_ROWS;
                        const capabilities = table?.export_capabilities || {};
                        const canCsv = capabilities.csv !== false;
                        const canPdf = capabilities.pdf === true;
                        const canZip = capabilities.zip !== false;
                        const showZip = Boolean(
                          canZip && (exportInfo.showZip || tooLargeForCsv || tooLargeForPdf)
                        );
                        const columns = Array.isArray(table?.columns) ? table.columns : [];
                        const rows = Array.isArray(table?.rows) ? table.rows : [];
                        const tableSummary =
                          table?.summary ||
                          (rowCount
                            ? `Showing ${rows.length} of ${rowCount} detail rows for this report.`
                            : "Showing a preview of the report details.");
                        return (
                          <div
                            key={`${exportToken || "table"}-${tableIndex}`}
                            className="rounded-2xl border border-slate-200 bg-white/80 p-3"
                          >
                            <div className="text-sm font-semibold text-slate-800">
                              {table?.title || "Report Table"}
                            </div>
                            <div className="text-xs text-slate-500">
                              {rowCount ? `${rowCount} rows` : "Preview"}
                            </div>
                            <div className="mt-1 text-xs text-slate-600">
                              {tableSummary}
                            </div>
                              {columns.length > 0 && rows.length > 0 && (
                                <div className="mt-2 overflow-x-auto">
                                  <table className="min-w-full text-xs text-slate-700">
                                    <thead className="bg-slate-100 text-slate-700">
                                      <tr>
                                      {columns.map((col) => (
                                        <th
                                          key={col.key}
                                          className="px-2 py-1 text-left font-semibold"
                                        >
                                          {col.label || col.key}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row, rowIndex) => (
                                      <tr
                                        key={`${exportToken || "row"}-${rowIndex}`}
                                        className="border-t border-slate-100"
                                      >
                                        {columns.map((col) => (
                                          <td key={col.key} className="px-2 py-1">
                                            {row?.[col.key] ?? ""}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {rowCount > rows.length && (
                                <div className="mt-2 text-xs text-slate-500">
                                  Showing first {rows.length}. Narrow your filters to see more.
                                </div>
                              )}
                            {exportToken && (
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {canCsv && (
                                  <button
                                    type="button"
                                    onClick={() => handleExport(table, "csv")}
                                    disabled={exportInfo.loading}
                                    className="rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                                  >
                                    Download CSV for Excel
                                  </button>
                                )}
                                {canPdf && (
                                  <button
                                    type="button"
                                    onClick={() => handleExport(table, "pdf")}
                                    disabled={exportInfo.loading}
                                    className="rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                                  >
                                    Download PDF
                                  </button>
                                )}
                                {showZip && (
                                  <button
                                    type="button"
                                    onClick={() => handleExport(table, "zip")}
                                    disabled={exportInfo.loading}
                                    className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                                  >
                                    Download ZIP
                                  </button>
                                )}
                                {exportInfo.loading && (
                                  <span className="text-xs text-slate-500">Preparing...</span>
                                )}
                              </div>
                            )}
                            {exportInfo.error && (
                              <div className="mt-2 text-xs text-rose-600">
                                {exportInfo.error}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!isUser && msg.export_ready && (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white/80 p-3">
                      <div className="text-sm font-semibold text-slate-800">
                        {msg.export_ready?.title || "Report Export"}
                      </div>
                      {msg.export_ready?.rowCount ? (
                        <div className="text-xs text-slate-500">
                          {msg.export_ready.rowCount} rows
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">Ready to export</div>
                      )}
                      {(() => {
                        const exportToken = msg.export_ready?.export_token;
                        if (!exportToken) return null;
                        const exportInfo = exportState[exportToken] || {};
                        const rowCount = Number(msg.export_ready?.rowCount || 0);
                        const tooLargeForCsv = rowCount > MAX_CSV_ROWS;
                        const tooLargeForPdf = rowCount > MAX_PDF_ROWS;
                        const capabilities = msg.export_ready?.export_capabilities || {};
                        const canCsv = capabilities.csv !== false;
                        const canPdf = capabilities.pdf === true;
                        const canZip = capabilities.zip !== false;
                        const showZip = Boolean(
                          canZip && (exportInfo.showZip || tooLargeForCsv || tooLargeForPdf)
                        );
                        return (
                          <>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              {canCsv && (
                                <button
                                  type="button"
                                  onClick={() => handleExport(msg.export_ready, "csv")}
                                  disabled={exportInfo.loading}
                                  className="rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                                >
                                  Download CSV for Excel
                                </button>
                              )}
                              {canPdf && (
                                <button
                                  type="button"
                                  onClick={() => handleExport(msg.export_ready, "pdf")}
                                  disabled={exportInfo.loading}
                                  className="rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                                >
                                  Download PDF
                                </button>
                              )}
                              {showZip && (
                                <button
                                  type="button"
                                  onClick={() => handleExport(msg.export_ready, "zip")}
                                  disabled={exportInfo.loading}
                                  className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                                >
                                  Download ZIP
                                </button>
                              )}
                              {exportInfo.loading && (
                                <span className="text-xs text-slate-500">Preparing...</span>
                              )}
                            </div>
                            {exportInfo.error && (
                              <div className="mt-2 text-xs text-rose-600">
                                {exportInfo.error}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-white border border-slate-200 px-4 py-2 text-sm text-slate-500 flex items-center gap-2">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                Thinking...
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700 sm:px-4 sm:py-3">
            {error}
          </div>
        )}

        {(attachedImage || attachedFile) && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
              <div className="flex items-center gap-3">
                {attachedImage && attachedPreviewUrl ? (
                  <img
                    src={attachedPreviewUrl}
                    alt={attachedImage.name || "Attached image"}
                    className="h-10 w-10 rounded-lg object-cover"
                  />
                ) : null}
                <div>
                  <div className="font-semibold text-slate-700">
                    {attachedImage?.name || attachedFile?.name || "Attachment"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {attachedImage?.type || attachedFile?.type || "file"}
                    {formatFileSize(attachedImage?.size || attachedFile?.size)
                      ? ` • ${formatFileSize(attachedImage?.size || attachedFile?.size)}`
                      : ""}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={clearAttachments}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                disabled={isLoading}
              >
                Remove
              </button>
            </div>
            {attachError && (
              <div className="text-xs text-rose-600">{attachError}</div>
            )}
          </div>
        )}
        {attachError && !(attachedImage || attachedFile) && (
          <div className="text-xs text-rose-600">{attachError}</div>
        )}

        <div className="rounded-2xl">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept={allowedAttachmentTypes.join(",")}
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => {
                  if (!attachmentsAvailable) {
                    setAttachError("Attachments are not enabled on this server.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                className="rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                disabled={attachmentControlsDisabled || !attachmentsAvailable}
                aria-label="Attach a file"
                title={
                  attachmentsAvailable
                    ? "Attach a file (or drag & drop / paste an image)"
                    : "Attachments are not enabled on this server"
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 115.66 5.66l-9.19 9.19a2 2 0 11-2.83-2.83l8.49-8.49" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onPaste={handlePaste}
              placeholder={inputPlaceholder}
              className="flex-1 rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 sm:px-4"
              disabled={attachmentControlsDisabled}
            />
            <button
              type="submit"
              className="rounded-full bg-rose-500 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60 sm:px-4"
              disabled={
                attachmentControlsDisabled ||
                !(input.trim() || attachedImage || attachedFile)
              }
            >
              Send
            </button>
          </form>
          {attachmentsAvailable && (
            <div className="mt-1 text-xs text-slate-500">
              Tip: Drag & drop a file here, or paste an image (Ctrl+V).
            </div>
          )}
        </div>
      </div>

      {previewImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70">
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <button
              type="button"
              onClick={() => {
                setPreviewImageUrl(null);
                setPreviewImageName("");
              }}
              className="absolute -top-10 right-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow"
            >
              Close
            </button>
            <img
              src={previewImageUrl}
              alt={previewImageName || "Attachment preview"}
              className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
      </div>
    </SBINModal>
  );
}

AIAssistantModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialPrefillMessage: PropTypes.string,
  initialContext: PropTypes.object,
};
